import React, { ReactNode } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { ChevronRightIcon, InfoOutlineIcon, LockIcon } from "@chakra-ui/icons";
import {
  findHrmsLocation,
  getHrmsSection,
  HrmsNavItem,
  isHrmsNavItemVisible,
} from "../../Pages/employee/hrmsNav";

interface HrmsPageShellProps {
  /** Page heading; also the breadcrumb leaf fallback. */
  title: string;
  /** Optional one-line explanation under the heading. */
  description?: ReactNode;
  /**
   * Section this page belongs to (see `HRMS_SECTIONS` keys). When omitted the
   * section is resolved from the current pathname.
   */
  sectionKey?: string;
  /** Right-aligned slot beside the heading (e.g. an "Add" button). */
  actions?: ReactNode;
  children: ReactNode;
  /** Renders the shared permission-denied state instead of children. */
  noPermission?: boolean;
  /** Renders the shared centered spinner instead of children. */
  isLoading?: boolean;
  /** Renders the shared empty state instead of children. */
  isEmpty?: boolean;
  emptyText?: string;
}

const CenteredMessage = ({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) => (
  <Flex
    direction="column"
    justify="center"
    align="center"
    minH="45vh"
    gap={3}
    bg="white"
    border="1px dashed #d6e2e2"
    borderRadius="14px"
  >
    <Flex
      w="56px"
      h="56px"
      borderRadius="full"
      bg="#e8f3f3"
      color="#327171"
      align="center"
      justify="center"
    >
      {icon}
    </Flex>
    <Text fontSize="15px" color="gray.500" textAlign="center" m={0} maxW="420px">
      {children}
    </Text>
  </Flex>
);

export const HrmsPageShell = ({
  title,
  description,
  sectionKey,
  actions,
  children,
  noPermission,
  isLoading,
  isEmpty,
  emptyText = "No data to display.",
}: HrmsPageShellProps) => {
  const location = useLocation();

  const resolved = findHrmsLocation(location.pathname);
  const section = sectionKey ? getHrmsSection(sectionKey) : resolved?.section;
  const activePath = resolved?.item.path ?? location.pathname;
  const leafLabel = resolved?.item.label ?? title;

  const visibleItems: HrmsNavItem[] = (section?.items ?? []).filter((item) =>
    isHrmsNavItemVisible(item, section),
  );

  return (
    <Box className="main-wrapper" bg="#f5f7f8">
      <Box
        bg="white"
        borderBottom="1px solid #e2e8f0"
        px={{ base: 4, md: 6 }}
        pt={4}
        pb={visibleItems.length > 1 ? 0 : 4}
      >
        <Breadcrumb
          spacing="6px"
          separator={<ChevronRightIcon color="gray.400" />}
          fontSize="12px"
          mb={1}
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/hrms/dashboard" color="#327171">
              HRMS
            </BreadcrumbLink>
          </BreadcrumbItem>
          {section && (
            <BreadcrumbItem>
              <Text color="gray.500" mb={0}>
                {section.label}
              </Text>
            </BreadcrumbItem>
          )}
          <BreadcrumbItem isCurrentPage>
            <Text color="gray.600" mb={0}>
              {leafLabel}
            </Text>
          </BreadcrumbItem>
        </Breadcrumb>

        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
          mb={visibleItems.length > 1 ? 2 : 0}
        >
          <Box minW={0}>
            <Text
              as="h1"
              fontSize="22px"
              fontWeight="700"
              color="#1d4747"
              lineHeight="1.3"
              m={0}
            >
              {title}
            </Text>
            {description && (
              <Text fontSize="13px" color="gray.500" m={0} mt={0.5}>
                {description}
              </Text>
            )}
          </Box>
          {actions && (
            <Flex align="center" gap={2} wrap="wrap" flexShrink={0}>
              {actions}
            </Flex>
          )}
        </Flex>

        {visibleItems.length > 1 && (
          <Flex className="hrms-subnav" overflowX="auto" mx={-1}>
            {visibleItems.map((item) => (
              <RouterLink
                key={item.path}
                to={item.path}
                className={activePath === item.path ? "active" : ""}
              >
                {item.label}
              </RouterLink>
            ))}
          </Flex>
        )}
      </Box>

      <Box px={{ base: 4, md: 6 }} py={5}>
        {noPermission ? (
          <CenteredMessage icon={<LockIcon boxSize={5} />}>
            You don&apos;t have permission to access this page.
          </CenteredMessage>
        ) : isLoading ? (
          <Flex justify="center" align="center" minH="45vh">
            <Spinner size="xl" color="#308888" thickness="3px" />
          </Flex>
        ) : isEmpty ? (
          <CenteredMessage icon={<InfoOutlineIcon boxSize={5} />}>
            {emptyText}
          </CenteredMessage>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
};
